// =============================================================================
// BrightPath - Health API Client
// Package: BP-03-04-P1 (Health Frontend - Foundation)
// =============================================================================

import apiClient from '@/core/lib/api-client';
import type {
  HealthLog,
  HealthLogType,
  HealthLogParams,
  CreateHealthLogDTO,
  UpdateHealthLogDTO,
  HealthLogStats,
  SeizureStats,
  SleepStats,
  MoodStats,
  MedicationAdherenceStats,
  StatsParams,
  Medication,
  MedicationParams,
  CreateMedicationDTO,
  UpdateMedicationDTO,
  MedicationAdministration,
  LogMedicationAdministrationDTO,
  MedicationHistoryParams,
  TodaysMedication,
  ExportParams,
  PaginatedResponse,
} from '@/features/health/types/healthTypes';

// =============================================================================
// Health Log Types (Reference Data)
// =============================================================================

/**
 * Fetch all available health log types
 */
export async function getHealthLogTypes(): Promise<HealthLogType[]> {
  const response = await apiClient.get<HealthLogType[]>('/health/log-types');
  return response.data;
}

/**
 * Fetch a single health log type by ID or slug
 */
export async function getHealthLogType(idOrSlug: string): Promise<HealthLogType> {
  const response = await apiClient.get<HealthLogType>(`/health/log-types/${idOrSlug}`);
  return response.data;
}

// =============================================================================
// Health Logs
// =============================================================================

/**
 * Fetch paginated health logs for a child
 */
export async function getHealthLogs(
  childId: string,
  params?: HealthLogParams
): Promise<PaginatedResponse<HealthLog>> {
  const response = await apiClient.get<PaginatedResponse<HealthLog>>(
    `/children/${childId}/health-logs`,
    { params }
  );
  return response.data;
}

/**
 * Fetch a single health log by ID
 */
export async function getHealthLog(logId: string): Promise<HealthLog> {
  const response = await apiClient.get<HealthLog>(`/health-logs/${logId}`);
  return response.data;
}

/**
 * Create a new health log
 */
export async function createHealthLog(
  childId: string,
  data: CreateHealthLogDTO
): Promise<HealthLog> {
  const response = await apiClient.post<HealthLog>(
    `/children/${childId}/health-logs`,
    data
  );
  return response.data;
}

/**
 * Update an existing health log
 */
export async function updateHealthLog(
  logId: string,
  data: UpdateHealthLogDTO
): Promise<HealthLog> {
  const response = await apiClient.patch<HealthLog>(`/health-logs/${logId}`, data);
  return response.data;
}

/**
 * Delete a health log
 */
export async function deleteHealthLog(logId: string): Promise<void> {
  await apiClient.delete(`/health-logs/${logId}`);
}

/**
 * Fetch recent health logs (last N entries)
 */
export async function getRecentHealthLogs(
  childId: string,
  limit: number = 5
): Promise<HealthLog[]> {
  const response = await apiClient.get<HealthLog[]>(
    `/children/${childId}/health-logs/recent`,
    { params: { limit } }
  );
  return response.data;
}

// =============================================================================
// Health Log Statistics
// =============================================================================

/**
 * Fetch overall health log statistics for a child
 */
export async function getHealthLogStats(
  childId: string,
  params?: StatsParams
): Promise<HealthLogStats> {
  const response = await apiClient.get<HealthLogStats>(
    `/children/${childId}/health-logs/stats`,
    { params }
  );
  return response.data;
}

/**
 * Fetch seizure-specific statistics
 */
export async function getSeizureStats(
  childId: string,
  params?: StatsParams
): Promise<SeizureStats> {
  const response = await apiClient.get<SeizureStats>(
    `/children/${childId}/health-logs/stats/seizures`,
    { params }
  );
  return response.data;
}

/**
 * Fetch sleep-specific statistics
 */
export async function getSleepStats(
  childId: string,
  params?: StatsParams
): Promise<SleepStats> {
  const response = await apiClient.get<SleepStats>(
    `/children/${childId}/health-logs/stats/sleep`,
    { params }
  );
  return response.data;
}

/**
 * Fetch mood-specific statistics
 */
export async function getMoodStats(
  childId: string,
  params?: StatsParams
): Promise<MoodStats> {
  const response = await apiClient.get<MoodStats>(
    `/children/${childId}/health-logs/stats/mood`,
    { params }
  );
  return response.data;
}

/**
 * Fetch medication adherence statistics
 */
export async function getMedicationAdherenceStats(
  childId: string,
  params?: StatsParams
): Promise<MedicationAdherenceStats> {
  const response = await apiClient.get<MedicationAdherenceStats>(
    `/children/${childId}/medications/stats/adherence`,
    { params }
  );
  return response.data;
}

// =============================================================================
// Medications
// =============================================================================

/**
 * Fetch all medications for a child
 */
export async function getMedications(
  childId: string,
  params?: MedicationParams
): Promise<Medication[]> {
  const response = await apiClient.get<Medication[]>(
    `/children/${childId}/medications`,
    { params }
  );
  return response.data;
}

/**
 * Fetch a single medication by ID
 */
export async function getMedication(medicationId: string): Promise<Medication> {
  const response = await apiClient.get<Medication>(`/medications/${medicationId}`);
  return response.data;
}

/**
 * Create a new medication
 */
export async function createMedication(
  childId: string,
  data: CreateMedicationDTO
): Promise<Medication> {
  const response = await apiClient.post<Medication>(
    `/children/${childId}/medications`,
    data
  );
  return response.data;
}

/**
 * Update an existing medication
 */
export async function updateMedication(
  medicationId: string,
  data: UpdateMedicationDTO
): Promise<Medication> {
  const response = await apiClient.patch<Medication>(
    `/medications/${medicationId}`,
    data
  );
  return response.data;
}

/**
 * Delete a medication
 */
export async function deleteMedication(medicationId: string): Promise<void> {
  await apiClient.delete(`/medications/${medicationId}`);
}

/**
 * Discontinue a medication (soft delete - sets isActive to false)
 */
export async function discontinueMedication(
  medicationId: string,
  endDate?: string
): Promise<Medication> {
  const response = await apiClient.post<Medication>(
    `/medications/${medicationId}/discontinue`,
    { endDate }
  );
  return response.data;
}

/**
 * Reactivate a discontinued medication
 */
export async function reactivateMedication(medicationId: string): Promise<Medication> {
  const response = await apiClient.post<Medication>(
    `/medications/${medicationId}/reactivate`
  );
  return response.data;
}

// =============================================================================
// Medication Administration
// =============================================================================

/**
 * Log a medication administration (taken or skipped)
 */
export async function logMedicationAdministration(
  medicationId: string,
  data: LogMedicationAdministrationDTO
): Promise<MedicationAdministration> {
  const response = await apiClient.post<MedicationAdministration>(
    `/medications/${medicationId}/administrations`,
    data
  );
  return response.data;
}

/**
 * Fetch medication administration history
 */
export async function getMedicationHistory(
  medicationId: string,
  params?: MedicationHistoryParams
): Promise<PaginatedResponse<MedicationAdministration>> {
  const response = await apiClient.get<PaginatedResponse<MedicationAdministration>>(
    `/medications/${medicationId}/administrations`,
    { params }
  );
  return response.data;
}

/**
 * Update a medication administration record
 */
export async function updateMedicationAdministration(
  administrationId: string,
  data: Partial<LogMedicationAdministrationDTO>
): Promise<MedicationAdministration> {
  const response = await apiClient.patch<MedicationAdministration>(
    `/medication-administrations/${administrationId}`,
    data
  );
  return response.data;
}

/**
 * Delete a medication administration record
 */
export async function deleteMedicationAdministration(
  administrationId: string
): Promise<void> {
  await apiClient.delete(`/medication-administrations/${administrationId}`);
}

// =============================================================================
// Today's Medications
// =============================================================================

/**
 * Fetch today's medication schedule with status
 */
export async function getTodaysMedications(
  childId: string
): Promise<TodaysMedication[]> {
  const response = await apiClient.get<TodaysMedication[]>(
    `/children/${childId}/medications/today`
  );
  return response.data;
}

/**
 * Quick log a medication dose for today
 */
export async function quickLogMedicationDose(
  medicationId: string,
  scheduledTime: string
): Promise<MedicationAdministration> {
  const now = new Date().toISOString();
  return logMedicationAdministration(medicationId, {
    scheduledTime,
    actualTime: now,
    dosage: '', // Will use medication's default dosage
    wasSkipped: false,
  });
}

/**
 * Quick skip a medication dose
 */
export async function quickSkipMedicationDose(
  medicationId: string,
  scheduledTime: string,
  reason: string
): Promise<MedicationAdministration> {
  const now = new Date().toISOString();
  return logMedicationAdministration(medicationId, {
    scheduledTime,
    actualTime: now,
    dosage: '',
    wasSkipped: true,
    skipReason: reason,
  });
}

// =============================================================================
// Export
// =============================================================================

/**
 * Export health data as PDF, CSV, or Excel
 */
export async function exportHealthData(
  childId: string,
  params: ExportParams
): Promise<Blob> {
  const response = await apiClient.post(
    `/children/${childId}/health-logs/export`,
    params,
    {
      responseType: 'blob',
    }
  );
  return response.data;
}

/**
 * Generate a provider report (formatted PDF for medical visits)
 */
export async function generateProviderReport(
  childId: string,
  params: Omit<ExportParams, 'format'>
): Promise<Blob> {
  const response = await apiClient.post(
    `/children/${childId}/health-logs/provider-report`,
    params,
    {
      responseType: 'blob',
    }
  );
  return response.data;
}

// =============================================================================
// Attachments
// =============================================================================

/**
 * Upload an attachment for a health log
 */
export async function uploadHealthLogAttachment(
  file: File
): Promise<{ id: string; url: string }> {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await apiClient.post<{ id: string; url: string }>(
    '/health-logs/attachments',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
}

/**
 * Delete an attachment
 */
export async function deleteHealthLogAttachment(attachmentId: string): Promise<void> {
  await apiClient.delete(`/health-logs/attachments/${attachmentId}`);
}

// =============================================================================
// Bulk Operations
// =============================================================================

/**
 * Create multiple health logs at once (for importing data)
 */
export async function bulkCreateHealthLogs(
  childId: string,
  logs: CreateHealthLogDTO[]
): Promise<{ created: number; failed: number; errors: string[] }> {
  const response = await apiClient.post(
    `/children/${childId}/health-logs/bulk`,
    { logs }
  );
  return response.data;
}

/**
 * Delete multiple health logs
 */
export async function bulkDeleteHealthLogs(logIds: string[]): Promise<{ deleted: number }> {
  const response = await apiClient.post('/health-logs/bulk-delete', { ids: logIds });
  return response.data;
}

// =============================================================================
// Export all functions as a namespace for convenience
// =============================================================================

export const healthApi = {
  // Log Types
  getHealthLogTypes,
  getHealthLogType,
  
  // Health Logs
  getHealthLogs,
  getHealthLog,
  createHealthLog,
  updateHealthLog,
  deleteHealthLog,
  getRecentHealthLogs,
  
  // Statistics
  getHealthLogStats,
  getSeizureStats,
  getSleepStats,
  getMoodStats,
  getMedicationAdherenceStats,
  
  // Medications
  getMedications,
  getMedication,
  createMedication,
  updateMedication,
  deleteMedication,
  discontinueMedication,
  reactivateMedication,
  
  // Medication Administration
  logMedicationAdministration,
  getMedicationHistory,
  updateMedicationAdministration,
  deleteMedicationAdministration,
  getTodaysMedications,
  quickLogMedicationDose,
  quickSkipMedicationDose,
  
  // Export
  exportHealthData,
  generateProviderReport,
  
  // Attachments
  uploadHealthLogAttachment,
  deleteHealthLogAttachment,
  
  // Bulk
  bulkCreateHealthLogs,
  bulkDeleteHealthLogs,
};

export default healthApi;

